import { prefetchToothBasisSeries } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { debug } from '@/shared/lib';

import type { ToothSliceGeometry } from '../config';

import { getCachedToothBasisSeriesEntry } from './loadToothBasisSeriesEntry';
import { makeToothBasisSeriesCacheKey } from './makeToothBasisSeriesCacheKey';
import { makeToothBasisSeriesRequest } from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { resolveNeighbourToothIsos } from './resolveNeighbourToothIsos';
import { findToothSliceGeometryByIso } from './toothSliceGeometryRegistry';

type PrefetchPriority = 'background' | 'interactive';

type PrefetchTask = {
  cacheKey: string;
  input: ToothSliceGeometry;
  priority: PrefetchPriority;
  spacingOutMm?: number;
};

const requestedCacheKeys = new Set<string>();
const pendingTasks: PrefetchTask[] = [];

const activeState: {
  abortController: AbortController | null;
  cacheKey: string;
} = {
  abortController: null,
  cacheKey: '',
};

let isDraining = false;

let pickedCacheKey = '';

const isRunning = (cacheKey: string) => activeState.cacheKey === cacheKey;

const isWarmed = (cacheKey: string) =>
  Boolean(getCachedToothBasisSeriesEntry(cacheKey)) ||
  (requestedCacheKeys.has(cacheKey) && !isRunning(cacheKey));

const warmToothRoi = ({
  cacheKey,
  input,
  priority,
  signal,
  spacingOutMm,
}: PrefetchTask & { signal: AbortSignal }) =>
  prefetchToothBasisSeries(
    makeToothBasisSeriesRequest({ input, spacingOutMm }),
    signal,
    undefined,
    { priority },
  )
    .then((outcome) => {
      if (outcome.reason === 'aborted') {
        requestedCacheKeys.delete(cacheKey);
      }

      return outcome.reason;
    })
    .catch((error: unknown) => {
      requestedCacheKeys.delete(cacheKey);

      debug('[FullHeadMPR] Tooth ROI prefetch failed', error);

      return 'failed' as const;
    });

const enqueueNeighbours = ({ input, spacingOutMm }: PrefetchTask) => {
  resolveNeighbourToothIsos(input.numeration.iso).forEach((iso) => {
    const neighbourInput = findToothSliceGeometryByIso({
      iso,
      volumeFileID: input.volumeFileID,
    });

    if (!neighbourInput) {
      return;
    }

    enqueuePrefetch({
      cacheKey: makeToothBasisSeriesCacheKey({
        input: neighbourInput,
        spacingOutMm,
      }),
      input: neighbourInput,
      priority: 'background',
      spacingOutMm,
    });
  });
};

const drainQueue = async () => {
  if (isDraining) {
    return;
  }

  isDraining = true;

  while (pendingTasks.length > 0) {
    const task = pendingTasks.shift();

    if (!task || isWarmed(task.cacheKey) || isRunning(task.cacheKey)) {
      continue;
    }

    const abortController = new AbortController();

    activeState.abortController = abortController;
    activeState.cacheKey = task.cacheKey;
    requestedCacheKeys.add(task.cacheKey);

    const reason = await warmToothRoi({
      ...task,
      signal: abortController.signal,
    });

    activeState.abortController = null;
    activeState.cacheKey = '';

    // Neighbours of neighbours would walk the whole quadrant.
    if (
      task.cacheKey === pickedCacheKey &&
      reason === 'complete' &&
      !abortController.signal.aborted
    ) {
      enqueueNeighbours(task);
    }
  }

  isDraining = false;
};

const insertByPriority = (task: PrefetchTask) => {
  const firstBackgroundIndex = pendingTasks.findIndex(
    (pending) => pending.priority === 'background',
  );

  if (task.priority === 'interactive' && firstBackgroundIndex >= 0) {
    pendingTasks.splice(firstBackgroundIndex, 0, task);

    return;
  }

  pendingTasks.push(task);
};

const enqueuePrefetch = (task: PrefetchTask) => {
  if (task.priority === 'interactive') {
    pickedCacheKey = task.cacheKey;
  }

  const pendingIndex = pendingTasks.findIndex(
    (pending) => pending.cacheKey === task.cacheKey,
  );

  if (pendingIndex >= 0) {
    const [queued] = pendingTasks.splice(pendingIndex, 1);

    insertByPriority(
      task.priority === 'interactive'
        ? { ...queued, priority: task.priority }
        : queued,
    );
    void drainQueue();

    return;
  }

  // Restarting it at a higher priority would throw away what it already fetched.
  if (isRunning(task.cacheKey)) {
    return;
  }

  if (isWarmed(task.cacheKey)) {
    if (task.cacheKey === pickedCacheKey) {
      enqueueNeighbours(task);
    }

    return;
  }

  insertByPriority(task);

  void drainQueue();
};

export const cancelToothBasisSeriesRoiPrefetch = (cacheKey: string) => {
  const pendingIndex = pendingTasks.findIndex(
    (task) => task.cacheKey === cacheKey,
  );

  if (pendingIndex >= 0) {
    pendingTasks.splice(pendingIndex, 1);

    return;
  }

  if (activeState.cacheKey !== cacheKey) {
    return;
  }

  activeState.abortController?.abort();
};

export const prefetchToothBasisSeriesRoi = ({
  cacheKey,
  input,
  spacingOutMm,
}: {
  cacheKey: string;
  input: ToothSliceGeometry;
  spacingOutMm?: number;
}) => {
  enqueuePrefetch({ cacheKey, input, priority: 'interactive', spacingOutMm });
};
